import useSWR from 'swr';
import { useAuth } from './useAuth';
import { useTenant } from './useTenant';
import { apiClient } from '@/api';

export type UserRole = 'owner' | 'admin' | 'member' | 'viewer';

interface CurrentUser {
  user_id: string;
  email: string;
  tenant_id: string;
  role: UserRole;
}

interface UseCurrentUserResult {
  currentUser: CurrentUser | null;
  isLoading: boolean;
  error: Error | null;
  /** Check if user has one of the specified roles */
  hasRole: (...roles: UserRole[]) => boolean;
  /** Check if user can manage team (owner or admin) */
  canManageTeam: boolean;
}

export function useCurrentUser(): UseCurrentUserResult {
  const { session } = useAuth();
  const { tenant } = useTenant();

  const fetcher = async (url: string): Promise<CurrentUser> => {
    if (!session?.access_token) {
      throw new Error('No authentication token');
    }
    return apiClient.get<CurrentUser>(url, { token: session.access_token });
  };

  const { data, error, isLoading } = useSWR<CurrentUser>(
    session?.access_token && tenant?.id ? '/api/v1/me' : null,
    fetcher,
    {
      revalidateOnFocus: false,
      dedupingInterval: 30000, // Cache for 30 seconds
    }
  );

  const hasRole = (...roles: UserRole[]): boolean => {
    if (!data?.role) return false;
    return roles.includes(data.role);
  };

  return {
    currentUser: data ?? null,
    isLoading,
    error: error ?? null,
    hasRole,
    canManageTeam: hasRole('owner', 'admin'),
  };
}
